import React from "react";
import {Button, Card, CardBody, CardHeader, Dropdown, GdCheckbox, Input, Loader, Toast} from "gd-react";
import GridDuck from "gridduck";
import cookie from "react-cookies";
import SaveBar from "../../components/SaveBar/SaveBar";
import gatewayCommandSocket from "../../services/gatewayCommandSocket";
import {ZIGBEE_DEFAULT_SCAN_SECONDS} from "@gridduck/gateway-rpc";

// The join window the button opens, from the contract so the label and the radio agree.
const SCAN_SECONDS = ZIGBEE_DEFAULT_SCAN_SECONDS;

// GatewayOps for a Pi-Duck. Its support actions - pairing, log upload, the SSH tunnel - run over
// the command socket and answer directly, where GatewayOps sets config flags the legacy firmware
// picks up on its next check-in. MainPage chooses between the two by model number, because only
// pi-duck firmware serves the command channel.
class GatewayRpcOps extends React.Component {

	constructor(props) {
		super(props);
		let self = this;
		this.state = {
			updateRef: 0,
			staging: this.props.item.staging,
			beta: this.props.item.beta,
			debug: this.props.item.debugMode,
			enhancedLogging: this.props.item.enhancedLogging,
			zigbeeDisabled: this.props.item.zigbeeDisabled,
			scanningOpen: false
		};
		this.stagingChange = this.stagingChange.bind(this);
		this.betaChange = this.betaChange.bind(this);
		this.enhancedLoggingChange = this.enhancedLoggingChange.bind(this);
		this.debugChange = this.debugChange.bind(this);
		this.zigbeeDisabledChange = this.zigbeeDisabledChange.bind(this);
		this.startScanning = this.startScanning.bind(this);
		this.stopScanning = this.stopScanning.bind(this);
		this.confirmRebootChange = this.confirmRebootChange.bind(this);
		this.confirmZigbeeResetChange = this.confirmZigbeeResetChange.bind(this);
		this.updatedExternally = this.updatedExternally.bind(this);
		this.saveChanges = this.saveChanges.bind(this);
		this.cancelChanges = this.cancelChanges.bind(this);
		this.hasChanged = this.hasChanged.bind(this);
		this.reboot = this.reboot.bind(this);
		this.resetOwners = this.resetOwners.bind(this);
		this.downloadLogs = this.downloadLogs.bind(this);
		this.downloadEnhancedLogs = this.downloadEnhancedLogs.bind(this);
		this.activateSSHTunnel = this.activateSSHTunnel.bind(this);
		this.zigbeeReset = this.zigbeeReset.bind(this);
		if (props.item) {
			props.item.on('updated', this.updatedExternally);
			this.updatedExternally();
		}
	}

	reboot() {
		return this.props.item.set({
			doReboot: true,
		});
	}

	zigbeeReset() {
		return this.props.item.set({
			doZigbeeReset: true,
		});
	}

	hasChanged() {
		return this.state.beta !== this.props.item.beta || this.state.staging !== this.props.item.staging || this.state.enhancedLogging !== this.props.item.enhancedLogging || this.state.debug !== this.props.item.debugMode || this.state.zigbeeDisabled !== this.props.item.zigbeeDisabled;
	}

	confirmRebootChange(val) {
		this.setState({confirmReboot: val.target.checked})
	}

	confirmZigbeeResetChange(val) {
		this.setState({confirmZigbeeReset: val.target.checked})
	}

	stagingChange(val) {
		this.setState({staging: val.target.checked})
	}

	betaChange(val) {
		this.setState({beta: val.target.checked});
	}

	// Fire and forget from the form's point of view: the window is opened on the hub now, not
	// saved for a config push later, so there is nothing here for the save bar to track.
	startScanning() {
		let self = this;
		this.setState({scanningResult: null});
		return gatewayCommandSocket
			.invoke(this.props.item.id, 'zigbee.permitJoin', {seconds: SCAN_SECONDS})
			.then(function () {
				self.setState({
					scanningOpen: true,
					scanningResult: 'Join window open for ' + (SCAN_SECONDS / 60) + ' minutes.',
				});
				// Reflect the hub closing it on its own, so the button does not sit on Stop for
				// a window that has already expired.
				self.scanningTimer = setTimeout(function () {
					self.setState({scanningOpen: false, scanningResult: 'Join window closed.'});
				}, SCAN_SECONDS * 1000);
			})
			.catch(function (err) {
				self.setState({scanningResult: 'Could not start scanning: ' + err.message});
				return Promise.reject(err);
			});
	}

	stopScanning() {
		let self = this;
		return gatewayCommandSocket
			.invoke(this.props.item.id, 'zigbee.permitJoin', {seconds: 0})
			.then(function () {
				clearTimeout(self.scanningTimer);
				self.setState({scanningOpen: false, scanningResult: 'Join window closed.'});
			})
			.catch(function (err) {
				self.setState({scanningResult: 'Could not stop scanning: ' + err.message});
				return Promise.reject(err);
			});
	}

	debugChange(val) {
		this.setState({debug: val.target.checked})
	}

	enhancedLoggingChange(val) {
		this.setState({enhancedLogging: val.target.checked})
	}

	zigbeeDisabledChange(val) {
		this.setState({zigbeeDisabled: val.target.checked})
	}

	updatedExternally(field) {
		let self = this;
		this.setState({
			updateRef: this.state.updateRef++,
			staging: this.props.item.staging,
			beta: this.props.item.beta,
			debug: this.props.item.debugMode,
			enhancedLogging: this.props.item.enhancedLogging,
			zigbeeDisabled: this.props.item.zigbeeDisabled
		});
	}

	componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS) {
		let self = this;
		if (prevProps !== this.props || this.props.item.id !== prevProps.item.id) {
			if (prevProps.item) {
				prevProps.item.off('updated', this.updatedExternally);
			}
			this.props.item.on('updated', this.updatedExternally);
			this.updatedExternally();
		}
	}

	componentWillUnmount() {
		if (this.props.item && this.props.item.off) {
			this.props.item.off('updated', this.updatedExternally);
		}
		// Otherwise it fires setState on an unmounted component once the window expires.
		clearTimeout(this.scanningTimer);
	}

	cancelChanges() {
		this.setState({
			staging: this.props.item.staging,
			beta: this.props.item.beta,
			debug: this.props.item.debugMode,
			enhancedLogging: this.props.item.enhancedLogging,
			zigbeeDisabled: this.props.item.zigbeeDisabled
		});
	}

	// scanning is no longer here: it is an action on the hub, not a setting to save.
	saveChanges() {
		return this.props.item.set({
			staging: this.state.staging,
			debugMode: this.state.debug,
			enhancedLogging: this.state.enhancedLogging,
			beta: this.state.beta,
			zigbeeDisabled: this.state.zigbeeDisabled
		});
	}

	// Over the command socket rather than the config-flag path: the device answers with the key
	// it wrote, so the file is already there when this resolves and there is nothing to poll for.
	downloadLogs() {
		let gateway = this.props.item;
		return gatewayCommandSocket.invoke(gateway.id, 'gateway.uploadLogs', {})
			.then((res) => {
				window.open('https://s3-eu-west-1.amazonaws.com/gridduck-gateway-logs/' + res.key, '_blank');
			});
	}

	// The port now comes back from the device, which is the only thing that knows which one the
	// tunnel actually bound. It used to be derived from the serial number, so a gateway whose
	// preferred port was taken sent support to a port belonging to someone else.
	activateSSHTunnel() {
		let gateway = this.props.item;
		let self = this;
		return gatewayCommandSocket.invoke(gateway.id, 'gateway.sshTunnel', {})
			.then((res) => {
				self.setState({
					tunnelPort: "ssh -t ubuntu@debug.gridduck.com -i ~/.ssh/debug_key.pem 'ssh -i keys/" + gateway.serialNumber + " root@localhost -p" + res.port + "; bash -l'"
				});
			});
	}

	// The old enhanced flag did two things at once - opened a tunnel and uploaded logs - because
	// one column had to carry both. Two methods now, run in order, each with its own outcome.
	downloadEnhancedLogs() {
		let gateway = this.props.item;
		let self = this;
		return gatewayCommandSocket.invoke(gateway.id, 'gateway.sshTunnel', {})
			.then((tunnel) => {
				self.setState({
					tunnelPort: "ssh -t ubuntu@debug.gridduck.com -i ~/.ssh/debug_key.pem 'ssh -i keys/" + gateway.serialNumber + " root@localhost -p" + tunnel.port + "; bash -l'"
				});
				return gatewayCommandSocket.invoke(gateway.id, 'gateway.uploadLogs', {});
			})
			.then((res) => {
				window.open('https://s3-eu-west-1.amazonaws.com/gridduck-gateway-logs/' + res.key, '_blank');
			});
	}

	resetOwners() {
		let gateway = this.props.item;
		let hub = {
			serialNumber: gateway.serialNumber,
			id: gateway.physicalGatewayId,
			resetOwners: true
		}
		
		return GridDuck.createPhysicalGateway(hub)
			.then((res) => {
				
				return Promise.resolve(true)
			})
			.catch((err) => Promise.reject(err))
		
	}

	render() {
		
		let self = this;
		let footerSaveBar = this.hasChanged() ?
			<SaveBar onSaveClick={this.saveChanges} onCancelClick={this.cancelChanges}/> : '';
		return (
			<div className={"page grey narrow not-flex"}>
				<div className={'column top'}>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Ops Settings'}/>
							<CardBody>
								<GdCheckbox checked={this.state.staging} name={'staging'} onChange={this.stagingChange}
											label={'Staging API Mode'}/>
								<br/>
								<GdCheckbox checked={this.state.beta} name={'beta'} onChange={this.betaChange}
											label={'Beta API Mode'}/>
								<br/>
								<GdCheckbox checked={this.state.debug} name={'debug'} onChange={this.debugChange}
											label={'Debug Mode'}/>
								<br/>
								<GdCheckbox checked={this.state.enhancedLogging} name={'enhancedLogging'} onChange={this.enhancedLoggingChange}
											label={'Enhanced Logging Mode'}/>
								<br/>
								<GdCheckbox checked={this.state.zigbeeDisabled} name={'zigbeeDisabled'}
											onChange={this.zigbeeDisabledChange} label={'Zigbee Disabled'}/>
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Logs & Debugging'}/>
							<CardBody>
								<h4>Download Logs</h4>
								<p>Uploads the hub's logs and opens them in a new tab.</p>
								<br/>
								<div style={{"width": "200px"}}>
									<Button
										progressRes
										color={'gd-blue'}
										label={'Download Logs'}
										onClick={this.downloadLogs}/>
								</div>
								<br/>
								<h4>Download Enhanced Logs</h4>
								<p>Opens the debug tunnel, then uploads the logs. The tunnel command appears below.</p>
								<br/>
								<div style={{"width": "200px"}}>
									<Button
										progressRes
										color={'gd-blue'}
										label={'Download Enhanced Logs'}
										onClick={this.downloadEnhancedLogs}/>
								</div>
								<br/>
								<h4>Debug Tunnel</h4>
								<p>Opens an SSH tunnel to the hub through debug.gridduck.com.</p>
								<br/>
								<div style={{"width": "200px"}}>
									<Button
										progressRes
										color={'gd-blue'}
										label={'Activate SSH Tunnel'}
										onClick={this.activateSSHTunnel}/>
								</div>
								{/* Shared by both tunnel-opening actions above, which is why they sit together. */}
								{!this.state.tunnelPort ? '' :
									(
										<p>{this.state.tunnelPort}</p>
									)
								}
								<br/>
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Zigbee'}/>
							<CardBody>
								<h4>Pairing</h4>
								<p>
									Opens the join window for {SCAN_SECONDS / 60} minutes. Factory-reset the device
									you are pairing while it is open.
								</p>
								<br/>
								<div style={{"width": "200px"}}>
									<Button
										progressRes
										color={'gd-blue'}
										label={this.state.scanningOpen ? 'Stop Scanning' : 'Start Scanning'}
										onClick={this.state.scanningOpen ? this.stopScanning : this.startScanning}/>
								</div>
								{this.state.scanningResult ?
									<p style={{fontSize: '11px', margin: '8px 0 0'}}>{this.state.scanningResult}</p> : null}
								<br/>
								<h4>Reset Zigbee</h4>
								<p>
									Caution: This will reboot the gateway and require all connected zigbee devices to be
									physically reset.
								</p>
								<br/>
								<GdCheckbox checked={this.state.confirmZigbeeReset} name={'confirmZigbeeReset'}
											onChange={this.confirmZigbeeResetChange} label={'Confirm Reset Zigbee'}/>
								<br/>
								<br/>
								{this.state.confirmZigbeeReset ?
									<Button onClick={self.zigbeeReset} progressRes additionalclasses={'sm'}
											color={'gd-red'}
											label={'Reset Zigbee'}/> : ''}
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Reboot'}/>
							<CardBody>
								<GdCheckbox checked={this.state.confirmReboot} name={'confirmReboot'}
											onChange={this.confirmRebootChange} label={'Confirm Reboot'}/>
								<br/>
								<br/>
								{this.state.confirmReboot ?
									<Button onClick={self.reboot} progressRes additionalclasses={'sm'} color={'gd-red'}
											label={'Reboot'}/> : ''}
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						<Card>
							<CardHeader title={'Reset Owners'}/>
							<CardBody>
								<div style={{"width": "200px"}}>
									<Button
										progressRes
										color={'gd-green'}
										label={'Reset Owners'}
										onClick={this.resetOwners}/>
								</div>

								<br/>
								<p>
									Caution: This will remove all owner organisations from the hub with the exception of
									GridDuck. For hub resale / reassignment.
								</p>
							</CardBody>
						</Card>
					</div>
					<div className={'detail-content'}>
						{footerSaveBar}
					</div>
				</div>
			</div>
		)
	}

}

export default GatewayRpcOps;
